import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { doPost, doFetch, doPut, doDelete, clearMyTour, s3upload } from '../../../redux/api/actions';
import { CREATE_TOUR, GET_MYTOUR, EDIT_TOUR, DELETE_TOUR, GET_PRESIGNED_IMAGE_URLS } from '../../../redux/api/constants';
import { Link, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import Layout from "../../layout";
import ImageUploader from "../../imageUploader";
import AudioTrackManager from "./AudioTrackManager";
import TourSharingModal from "./TourSharingModal";
import { useTranslation } from 'react-i18next';

const categoryOptions = [
    "General",
    "Ghost Tour",
    "Historical",
    "Pop Culture",
    "Family History",
    "Food & Drink",
    "Architecture",
    "Nature & Parks",
    "Art & Culture"
];

const categoryTranslationKeys = {
    "General": "category-general",
    "Ghost Tour": "category-ghost-tour",
    "Historical": "category-historical",
    "Pop Culture": "category-pop-culture",
    "Family History": "category-family-history",
    "Food & Drink": "category-food-drink",
    "Architecture": "category-architecture",
    "Nature & Parks": "category-nature-parks",
    "Art & Culture": "category-art-culture",
};

function EditTour() {
    const { t } = useTranslation();
    const { tourId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tour = useSelector(state => state.API.myTour);

    const [title, setTitle] = useState('');
    const [titleError, setTitleError] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [imageUrls, setImageUrls] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showSharingModal, setShowSharingModal] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [category, setCategory] = useState('General');
    const [privacyLevel, setPrivacyLevel] = useState('PUBLIC');

    const accessToken = useSelector(state => state.Auth.accessToken);
    const presignedImageUrls = useSelector(state => state.API.presignedImageUrls);

    useEffect(() => {
        if (tourId) {
            dispatch(doFetch(GET_MYTOUR, `tourguide/tours/${tourId}`, accessToken));
        } else {
            dispatch(clearMyTour());
        }
    }, [accessToken, tourId, dispatch]);

    useEffect(() => {
        if (tour) {
            setTitle(tour.title || '');
            setDescription(tour.description || '');
            setImageUrls(tour.image_urls || []);
            setCategory(tour.category || 'General');
            setPrivacyLevel(tour.privacy_level || 'PUBLIC');
        } else {
            setTitle('');
            setDescription('');
            setImageUrls([]);
            setCategory('General');
            setPrivacyLevel('PUBLIC');
        }
    }, [tour]);

    useEffect(() => {
        if (presignedImageUrls && presignedImageUrls.length > 0) {
            const processedImageUrls = presignedImageUrls.map(url => url.split('?')[0]);
            setImageUrls(processedImageUrls.join(','));
            dispatch(s3upload(imageFiles, presignedImageUrls));
        }
    }, [presignedImageUrls, dispatch, imageFiles]);

    const deleteTour = () => {
        dispatch(doDelete(DELETE_TOUR, `tourguide/tours/${tourId}`, accessToken, navigate));
    };

    const validateAndSubmit = (e) => {
        e.preventDefault();

        setTitleError('');
        setDescriptionError('');

        let errors = 0;

	    if (!title.trim()) {
	            setTitleError(t('tour-title-required'));
	            errors++;
	        }
	        if (!description.trim()) {
	            setDescriptionError(t('tour-description-required'));
	            errors++;
	        }

        if (errors > 0) return;

        const params = {
            title,
            description,
            image_urls: imageUrls && imageUrls.length > 0 ? imageUrls : "",
            category,
            privacy_level: privacyLevel,
        };

        if (tourId) {
            dispatch(doPut(EDIT_TOUR, `tourguide/tours/${tourId}`, accessToken, params, navigate));
        } else {
            dispatch(doPost(CREATE_TOUR, "tourguide/tours", accessToken, params, navigate));
        }
    };

    const imageUploadFunc = (files) => {
        const fileNames = Array.from(files).map((file) => file.name);
        dispatch(doPost(GET_PRESIGNED_IMAGE_URLS, "tourguide/presigned-urls", accessToken, { images: fileNames }));
        setImageFiles(files);
    };

    return (
        <Layout selected="tourguide">
            <div className="tour-editor">
                <nav aria-label="breadcrumb" className="breadcrumb-nav">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
	                            <Link to="/">{t('home')}</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/tourguide#tab1">{t('tourguide-my-tours')}</Link>
                        </li>
                        <li className="breadcrumb-item active">
	                            {tourId ? t('tour-edit') : t('tour-create')}
	                        </li>
                    </ol>
                </nav>

                <div className="editor-header">
	                    <h1>{tourId ? t('tour-edit') : t('tour-create-new')}</h1>
	                    <p className="subtitle">{tourId ? t('tour-editor-subtitle-edit') : t('tour-editor-subtitle-create')}</p>
                </div>

                <form onSubmit={validateAndSubmit} className="tour-form">
                    <div className="form-section">
	                        <label className="form-section-title">{t('tour-images')}</label>
                        <ImageUploader
                            imageUrls={imageUrls && typeof imageUrls === 'string' ? imageUrls.split(',') : null}
                            imageUploadFunc={imageUploadFunc}
                            multiple={true}
                        />
                    </div>

                    <div className="form-section">
                        <label htmlFor="title" className="form-label">{t('tour-title')}</label>
                        <input
                            type="text"
                            id="title"
                            className={`form-input ${titleError ? 'error' : ''}`}
	                            placeholder={t('tour-title-placeholder')}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        {titleError && <span className="error-text">{titleError}</span>}
                    </div>

                    <div className="form-section">
                        <label htmlFor="description" className="form-label">{t('tour-description')}</label>
                        <textarea
                            id="description"
                            className={`form-input textarea ${descriptionError ? 'error' : ''}`}
	                            placeholder={t('tour-description-placeholder')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="4"
                        />
                        {descriptionError && <span className="error-text">{descriptionError}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-section half">
	                            <label htmlFor="category" className="form-label">{t('tour-category')}</label>
                            <select
                                id="category"
                                className="form-input"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {categoryOptions.map((option) => (
                                    <option key={option} value={option}>
	                                        {t(categoryTranslationKeys[option])}
	                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-section half">
	                            <label className="form-label">{t('tour-privacy-level')}</label>
                            <div className="privacy-options">
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        name="privacy"
                                        value="PUBLIC"
                                        checked={privacyLevel === 'PUBLIC'}
                                        onChange={(e) => setPrivacyLevel(e.target.value)}
                                    />
                                    <div className="radio-content">
	                                        <strong>{t('tour-public')}</strong>
	                                        <span>{t('tour-public-description')}</span>
                                    </div>
                                </label>
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        name="privacy"
                                        value="PRIVATE"
                                        checked={privacyLevel === 'PRIVATE'}
                                        onChange={(e) => setPrivacyLevel(e.target.value)}
                                    />
                                    <div className="radio-content">
	                                        <strong>{t('tour-private')}</strong>
	                                        <span>{t('tour-private-description')}</span>
                                    </div>
                                </label>
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        name="privacy"
                                        value="UNLISTED"
                                        checked={privacyLevel === 'UNLISTED'}
                                        onChange={(e) => setPrivacyLevel(e.target.value)}
                                    />
                                    <div className="radio-content">
	                                        <strong>{t('tour-unlisted')}</strong>
	                                        <span>{t('tour-unlisted-description')}</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-primary">
                            {tourId ? t('tour-update') : t('tour-create')}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => navigate("/tourguide#tab1")}>
	                            {t('cancel')}
                        </button>
                        {tourId && (
                            <>
                                {privacyLevel === 'PRIVATE' && (
                                    <button type="button" className="btn-outline" onClick={() => setShowSharingModal(true)}>
	                                        {t('tour-manage-sharing')}
                                    </button>
                                )}
                                <button type="button" className="btn-danger" onClick={() => setShowModal(true)}>
                                    {t('tour-delete')}
                                </button>
                            </>
                        )}
                    </div>
                </form>

                {/* Audio Track Manager - Only show after tour is created */}
                {tourId && (
                    <div className="audio-tracks-section">
                        <AudioTrackManager tourId={tourId} />
                    </div>
                )}

                {/* Tour Sharing Modal */}
                {showSharingModal && (
                    <TourSharingModal
                        tourId={tourId}
                        onClose={() => setShowSharingModal(false)}
                    />
                )}

                {/* Delete Confirmation Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
	                            <h3>{t('tour-delete-confirm-title')}</h3>
	                            <p>{t('tour-delete-confirm-message')}</p>
                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => setShowModal(false)}>
	                                    {t('cancel')}
                                </button>
                                <button className="btn-danger" onClick={deleteTour}>
	                                    {t('delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default EditTour;
